import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {ClauseService} from '../core/ecv-http-services/services/clause.service';
import {UserService} from '../core/ecv-http-services/services/user.service';
import {StepComponentAbstract} from '../flow/step.component';
import {ValidatorService} from '../core/validator/validator.service';
import {AlertService} from '../widgets/alert/alert.service';
import {ClauseDescriptionModel} from './clause-description.model';
import {ClauseSubtype} from './clause-subtype.enum';
import {ClauseTag} from './clause-tag.enum';
import {ClauseType} from './clause-type.enum';
import {ClauseModel} from './clause.model';
import {IncludeCompanyType} from './include-company-type.enum';
import {Observable} from 'rxjs/Observable';

@Component({
  selector: 'app-expert-clauses',
  templateUrl: './expert-clauses.component.html',
  styleUrls: ['./expert-clauses.component.scss']
})
export class ExpertClausesComponent extends StepComponentAbstract implements OnInit {
  clauses: ClauseModel[] = [];
  userId: number;
  userIdentity: string;
  foundClauseDescriptions: ClauseDescriptionModel[] = [];
  clauseTag = 0;
  clauseTags: ClauseTag[];
  clauseTypes: ClauseType[];
  clauseSubtypes: ClauseSubtype[];
  includeCompanyTypes: IncludeCompanyType[];
  clausesMapping: number[][];

  constructor(private clauseService: ClauseService,
              private translateService: TranslateService,
              private validatorService: ValidatorService,
              private alertService: AlertService,
              private route: ActivatedRoute,
              private router: Router,
              private userService: UserService) {
    super();
  }

  ngOnInit() {
    this.initializeClauseTags();
    this.initializeClauseTypes();
    this.initializeClauseSubtype();
    this.initializeIncludeCompanyType();
    this.createForm();
    this.route.params.subscribe(params => {
      if (!params) {
        this.router.navigate(['/process-clauses']);
      }
      this.loadData(params['id']);
    });
    this.clausesMapping = [[0, 1, 2, 4, 5], [3, 5], [4], [0, 4, 7], [0, 1, 4, 6], [0, 1, 6], [0, 1, 6], [0, 1, 6],
      [0, 1, 4, 6], [0, 1, 6], [0, 1, 6], [0, 1, 4, 6]];
  }

  initializeClauseTags() {
    this.clauseTags = [];
    for (const n in ClauseTag) {
      if (typeof ClauseTag[n] === 'number') {
        this.clauseTags.push(ClauseTag[ClauseTag[n]]);
      }
    }
  }

  initializeClauseTypes() {
    this.clauseTypes = [];
    for (const n in ClauseType) {
      if (typeof ClauseType[n] === 'number') {
        this.clauseTypes.push(ClauseType[ClauseType[n]]);
      }
    }
  }

  initializeClauseSubtype() {
    this.clauseSubtypes = [];
    for (const n in ClauseSubtype) {
      if (typeof ClauseSubtype[n] === 'number') {
        this.clauseSubtypes.push(ClauseSubtype[ClauseSubtype[n]]);
      }
    }
  }

  initializeIncludeCompanyType() {
    this.includeCompanyTypes = [];
    for (const n in IncludeCompanyType) {
      if (typeof IncludeCompanyType[n] === 'number') {
        this.includeCompanyTypes.push(IncludeCompanyType[IncludeCompanyType[n]]);
      }
    }
  }

  loadData(userId): void {
    this.userId = userId;
    this.clauseService.getAllClauses(userId).subscribe(
      response => {
        this.clauses = response;
      }
    );
    this.userService.getUserIdentity(userId).subscribe(
      response => {
        this.userIdentity = response.identity;
      }
    );
  }

  updateClauseTag(): void {
    this.foundClauseDescriptions = [];
    const clauseTypes = this.clausesMapping[this.clauseTag];
    for (let i = 0; i < clauseTypes.length; i++) {
      this.clauseService.getAllClauseDescriptions(clauseTypes[i]).subscribe(
        response => {
          this.foundClauseDescriptions = this.foundClauseDescriptions.concat(
            response.filter(x => x.tag === this.clauseTag));
        }
      );
    }
  }

  onSave(): Observable<any> {
    if (this.isSaveInProgress) {
      this.showEcvAlert(true);
      return Observable.of(true);
    }
    this.validatorService.validate(this.stepForm);
    if (!this.validatorService.isValid(this.stepForm)) {
      this.showEcvAlert(false);
      return Observable.of(false);
    }

    let changes: Observable<any>[] = this.updateChanges();
    if (changes.length === 0) {
      this.showEcvAlert(true);
      return Observable.of(true);
    } else {
      this.isSaveInProgress = true;
      return Observable.forkJoin(changes)
        .map(() => {
          this.isSaveInProgress = false;
          this.showEcvAlert(true);
        }, () => {
          this.isSaveInProgress = false;
          this.showEcvAlert(false);
        });
    }
  }

  private showEcvAlert(valid: boolean) {
    if (valid) {
      this.translateService.get('ALERT.CLAUSE_SAVED', {}).subscribe((res: string) => {
        this.alertService.success(res);
      });
    } else {
      this.translateService.get('ALERT.FIX_ERRORS', {}).subscribe((res: string) => {
        this.alertService.error(res);
      });
    }
  }

  removeClause(index: number) {
    this.clauses[index].deleted = true;
  }

  addClause() {
    const newItem: ClauseModel = new ClauseModel();
    newItem.userId = this.userId;
    this.clauses.push(newItem);
  }

  createClause(template: ClauseDescriptionModel) {
    const newItem: ClauseModel = new ClauseModel();
    newItem.userId = this.userId;
    newItem.description = template.description;
    newItem.type = template.type;
    newItem.subtype = template.subtype;
    newItem.tag = template.tag;
    newItem.includeCompanyType = template.includeCompanyType;
    this.clauses.unshift(newItem);
  }

  updateChanges(): Observable<any>[] {
    let changes: Observable<any>[] = [];
    for (const clause of this.clauses) {
      if (clause.id && clause.deleted) {
        changes.push(this.clauseService.deleteClause(clause));
      } else if (clause.deleted) {
        continue;
      } else if (clause.id) {
        changes.push(this.clauseService.updateClause(clause));
      } else {
        changes.push(this.clauseService.createClause(clause));
      }
    }
    return changes;
  }

  private createForm(): void {
    this.stepForm = new FormGroup({
      'clauseTag': new FormControl('')
    });
  }

  subscribeChanges() {
    this.onSave().subscribe(() => {
    }, () => {
    });
  }
}



// WEBPACK FOOTER //
// C:/Users/Sylwia/Desktop/12.02/ecv_manager_frontend/src/app/clause/expert-clauses.component.ts